import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { Breadcrumb } from 'primeng/breadcrumb';
import { BaseItem } from '../../../interfaces/api-response.interfaces';
import { MenuItem } from 'primeng/api';
import { CronFriendlyPipe } from '../../../pipes/cron.pipes';
import { RouterLink } from '@angular/router';
import { ProgressSpinner } from 'primeng/progressspinner';
import { NavigationStoreService } from '../../../services/navigation-store.service';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { debounceTime, distinctUntilChanged, Observable, Subject, Subscription } from 'rxjs';
import { Tooltip } from 'primeng/tooltip';
import { Select } from 'primeng/select';
import { Menu } from 'primeng/menu';

export interface CardField {
  label: string;
  key: string;
  type?: 'text' | 'status' | 'pipe';
  pipe?: string;
  nestedKey?: string;
  tooltipKey?: string;
  statusConfig?: {
    activeValue: unknown;
    activeLabel: string;
    inactiveLabel: string;
    activeClass: string;
    inactiveClass: string;
  };
}

type StatusTone = 'active' | 'warning' | 'danger';

export interface CardConfig {
  titleKey: string;
  titleTooltipKey?: string;
  fields: CardField[];
  clickable?: boolean;
  themeCount?: number;
  visibleMenuActions?: CardActionType[];
}

export interface DataSourceConfig {
  dataPath?: string;
  totalRecordsPath?: string;
  totalPagesPath?: string;
  pageIndexPath?: string;
  successPath?: string;
}

export type StatusFilterValue = 'all' | 'true' | 'false';
export type CardActionType = 'run-now' | 'toggle' | 'restart' | 'delete' | 'view-more';

const DEFAULT_VISIBLE_MENU_ACTIONS: CardActionType[] = ['view-more', 'run-now'];

@Component({
  selector: 'lib-card-list',
  imports: [
    CommonModule,
    Paginator,
    Breadcrumb,
    RouterLink,
    ProgressSpinner,
    Button,
    InputText,
    Tooltip,
    FormsModule,
    Select,
    Menu,
  ],
  templateUrl: './card-list.component.html',
  standalone: true,
})
export class CardListComponent<T extends BaseItem = BaseItem>
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() dataSource$!: Observable<unknown>;
  @Input() cardConfig!: CardConfig;
  @Input() dataSourceConfig: DataSourceConfig = {
    dataPath: 'data',
    totalRecordsPath: 'total',
    totalPagesPath: '',
    pageIndexPath: 'page',
    successPath: '',
  };
  @Input() key?: number;
  @Input() breadcrumbs = signal<MenuItem[]>([]);
  @Input() useDynamicBreadcrumbs = true;
  @Input() breadcrumbRoute = 'monitoring';
  @Input() searchPlaceholder = 'Workflow...';
  @Input() noResultsMessage = 'No se encontraron resultados';
  @Input() showPagination = true;
  @Input() initialLimit = 10;
  @Input() enableSearch = true;
  @Input() showStatusFilter = false;
  @Input() statusFilterValue: StatusFilterValue = 'all';
  @Input() searchTerm = '';
  @Input() showClearFiltersButton = false;
  @Input() hasActiveFilters = false;
  @Input() isLoading = signal(false);

  @Input() emptyStateTitle = 'No hay elementos disponibles';
  @Input() emptyStateDescription =
    'Actualmente no hay información para mostrar. Los datos aparecerán aquí una vez que estén disponibles.';
  @Input() emptyStateSubtext =
    'Verifica tu conexión o contacta al administrador si el problema persiste';
  @Input() showEmptyStateAction = false;
  @Input() emptyStateActionLabel = 'Recargar';
  @Input() emptyStateActionIcon = 'pi pi-refresh';

  @Output() itemClick = new EventEmitter<T>();
  @Output() pageChange = new EventEmitter<{
    limit: number;
    offset: number;
    page?: number;
  }>();
  @Output() searchChange = new EventEmitter<{
    search: string;
    limit: number;
    offset: number;
    page?: number;
  }>();
  @Output() statusFilterChange = new EventEmitter<StatusFilterValue>();
  @Output() cardAction = new EventEmitter<{ action: CardActionType; item: T }>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() emptyStateAction = new EventEmitter<void>();

  public readonly statusFilterOptions: Array<{ label: string; value: StatusFilterValue }> = [
    { label: 'Todos los estados', value: 'all' },
    { label: 'Solo activos', value: 'true' },
    { label: 'Solo inactivos', value: 'false' },
  ];

  public items = signal<T[]>([]);
  public filteredItems = signal<T[]>([]);
  public limit = signal<number>(this.initialLimit);
  public offset = signal<number>(0);
  public totalRecords = signal<number>(0);
  public totalPages = signal<number>(0);
  public currentPage = signal<number>(1);

  public paginatorFirst = computed(() => {
    return (this.currentPage() - 1) * this.limit();
  });
  public searchValue = signal<string>('');
  @ViewChild('searchInput', { read: ElementRef }) searchInputRef?: ElementRef<HTMLInputElement>;
  private readonly navigationStoreService = inject(NavigationStoreService);
  public displayBreadcrumbs = computed(() => {
    if (this.useDynamicBreadcrumbs) {
      return this.navigationStoreService.generateBreadcrumbs(this.breadcrumbRoute);
    }
    return this.breadcrumbs();
  });
  private readonly cronFriendlyPipe = new CronFriendlyPipe();
  private readonly subscription?: Subscription;
  private readonly searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private shouldRestoreFocus = false;
  private readonly menuHideTimeouts = new Map<Menu, ReturnType<typeof setTimeout>>();
  public readonly activeMenuItems = signal<MenuItem[]>([]);

  ngOnInit(): void {
    this.setupSearch();
    this.loadData();

    if (this.useDynamicBreadcrumbs && this.breadcrumbRoute === 'monitoring') {
      this.navigationStoreService.setMonitoringBreadcrumbs();
    }

    // Configurar debounce para la búsqueda
    if (this.enableSearch) {
      this.searchSubscription = this.searchSubject
        .pipe(
          debounceTime(300),
          distinctUntilChanged(), // Solo emitir si el valor cambió
        )
        .subscribe((searchValue) => {
          // Verificar si el input tenía el foco antes de la búsqueda
          const hadFocus = document.activeElement === this.searchInputRef?.nativeElement;

          this.searchValue.set(searchValue);
          // Resetear a la primera página cuando se busca
          this.currentPage.set(1);
          this.offset.set(0);

          this.searchChange.emit({
            search: searchValue,
            limit: this.limit(),
            offset: 0,
            page: 1,
          });

          // Marcar que debemos restaurar el foco después de que se carguen los datos
          if (hadFocus) {
            this.shouldRestoreFocus = true;
          }
        });
    }
  }

  ngAfterViewInit(): void {
    // Si necesitamos restaurar el foco y el componente se acaba de inicializar
    if (this.shouldRestoreFocus && this.enableSearch && !this.isLoading()) {
      this.restoreFocus();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar cuando cambia dataSource$ o key
    if (changes['dataSource$'] || changes['key']) {
      // Solo recargar si no es el primer cambio (ngOnInit ya lo maneja)
      if (!changes['dataSource$']?.firstChange && !changes['key']?.firstChange) {
        this.loadData();
      }
    }

    if (changes['searchTerm']) {
      this.searchValue.set(this.searchTerm ?? '');
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
    this.menuHideTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.menuHideTimeouts.clear();
  }

  public onEmptyStateAction(): void {
    this.emptyStateAction.emit();
  }

  public onPageChange(event: PaginatorState): void {
    if (event.first !== undefined && event.rows !== undefined) {
      const page = Math.floor(event.first / event.rows) + 1;
      this.offset.set(event.first);
      this.limit.set(event.rows);
      this.currentPage.set(page);

      this.pageChange.emit({
        limit: event.rows,
        offset: event.first,
        page: page,
      });
    }
  }

  public getCardTheme(index: number): string {
    const themes = ['blue', 'gray', 'teal', 'indigo', 'green'];
    return themes[index % themes.length] || themes[0];
  }

  public getThemeClasses(theme: string): string {
    const themeClasses: Record<string, string> = {
      blue: 'bg-gradient-to-br from-blue-50 to-sky-100 dark:from-blue-900 dark:to-sky-800',
      gray: 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700',
      teal: 'bg-gradient-to-br from-teal-50 to-cyan-100 dark:from-teal-900 dark:to-cyan-800',
      indigo:
        'bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900 dark:to-indigo-800',
      green:
        'bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900 dark:to-green-800',
    };
    return themeClasses[theme] || themeClasses['blue'];
  }

  public onCardClick(item: T): void {
    if (this.cardConfig.clickable) {
      this.itemClick.emit(item);
    }
  }

  public getCardMenuItems(item: T): MenuItem[] {
    const isEnabled = this.isItemEnabled(item);
    const visibleActions = this.cardConfig?.visibleMenuActions ?? DEFAULT_VISIBLE_MENU_ACTIONS;

    const actionItems: Record<CardActionType, MenuItem> = {
      'view-more': {
        label: 'Ver más',
        icon: 'pi pi-eye',
        command: () => this.onCardAction('view-more', item),
      },
      'run-now': {
        label: 'Ejecutar ahora',
        icon: 'pi pi-play-circle',
        command: () => this.onCardAction('run-now', item),
      },
      toggle: {
        label: isEnabled ? 'Desactivar' : 'Activar',
        icon: 'pi pi-pause',
        command: () => this.onCardAction('toggle', item),
      },
      restart: {
        label: 'Reiniciar',
        icon: 'pi pi-refresh',
        command: () => this.onCardAction('restart', item),
      },
      delete: {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        styleClass: 'text-red-600 dark:text-red-400',
        command: () => this.onCardAction('delete', item),
      },
    };

    return visibleActions.map((action) => actionItems[action]);
  }

  public openCardMenu(event: MouseEvent, menu: Menu, item: T): void {
    this.clearCardMenuHide(menu);
    this.activeMenuItems.set(this.getCardMenuItems(item));
    menu.show(event);
  }

  public scheduleCardMenuHide(menu: Menu, event?: MouseEvent): void {
    const nextTarget = event?.relatedTarget;
    const menuContainer = menu.container as HTMLElement | undefined;

    if (nextTarget instanceof Node && menuContainer?.contains(nextTarget)) {
      return;
    }

    this.clearCardMenuHide(menu);

    const timeoutId = setTimeout(() => {
      const triggerHovered =
        event?.currentTarget instanceof Element && event.currentTarget.matches(':hover');
      const menuHovered = Boolean(menuContainer?.matches(':hover'));

      if (!triggerHovered && !menuHovered) {
        menu.hide();
      }
    }, 120);

    this.menuHideTimeouts.set(menu, timeoutId);
  }

  public bindCardMenuHover(menu: Menu): void {
    const menuContainer = menu.container as HTMLElement | undefined;

    if (!menuContainer || menuContainer.dataset['hoverBound'] === 'true') {
      return;
    }

    menuContainer.dataset['hoverBound'] = 'true';
    menuContainer.addEventListener('mouseenter', () => this.clearCardMenuHide(menu));
    menuContainer.addEventListener('mouseleave', () => this.scheduleCardMenuHide(menu));
  }

  private clearCardMenuHide(menu: Menu): void {
    const timeoutId = this.menuHideTimeouts.get(menu);

    if (timeoutId) {
      clearTimeout(timeoutId);
      this.menuHideTimeouts.delete(menu);
    }
  }

  public getItemValue(item: T, key: string) {
    return this.getNestedValue(item, key) || '';
  }

  public getTitleTooltip(item: T) {
    const key = this.cardConfig?.titleTooltipKey || this.cardConfig?.titleKey;
    if (!key) {
      return '';
    }
    return String(this.getItemValue(item, key) ?? '');
  }

  public getFieldTooltip(item: T, field: CardField) {
    if (field.tooltipKey) {
      return String(this.getItemValue(item, field.tooltipKey) ?? '');
    }

    return String(this.getItemValue(item, field.key) ?? '');
  }

  public getFieldValue(item: T, field: CardField): string {
    const value = this.getNestedValue(item, field.nestedKey || field.key);

    if (field.type === 'status' && field.statusConfig) {
      return value === field.statusConfig.activeValue
        ? field.statusConfig.activeLabel
        : field.statusConfig.inactiveLabel;
    }

    if (field.type === 'pipe' && field.pipe) {
      switch (field.pipe) {
        case 'cronFriendly':
          return this.cronFriendlyPipe.transform(<string>value);
        default: {
          const stringValue = (value ?? '').toString().trim();
          return stringValue === '' ? '--' : stringValue;
        }
      }
    }

    if (value === null || value === undefined) {
      return '--';
    }

    if (typeof value === 'object') {
      return '--';
    }

    const stringValue = String(value).trim();
    return stringValue === '' ? '--' : stringValue;
  }

  public getFieldClass(item: T, field: CardField): string {
    if (field.type === 'status' && field.statusConfig) {
      const value = this.getNestedValue(item, field.nestedKey || field.key);
      const defaultActiveClass =
        'text-xs text-green-600 bg-green-100 py-1 px-2 rounded border border-green-200 dark:text-green-400';
      const defaultInactiveClass =
        'text-xs text-amber-700 bg-amber-100 py-1 px-2 rounded border border-amber-200 dark:text-amber-300';

      return value === field.statusConfig.activeValue
        ? field.statusConfig.activeClass || defaultActiveClass
        : field.statusConfig.inactiveClass || defaultInactiveClass;
    }

    return '';
  }

  public getFieldDisplayValue(item: T, key: string, fallback = '--'): string {
    const field = this.getFieldByKey(key);
    if (!field) {
      return fallback;
    }

    const value = this.getFieldValue(item, field);
    return value === '--' ? fallback : value;
  }

  public getDescriptionDisplayValue(item: T): string {
    return this.getFieldDisplayValue(item, 'description', 'Sin descripción');
  }

  public getExecutionsDisplayValue(item: T): string {
    return this.getFieldDisplayValue(item, 'total_run_count', '0');
  }

  public getStatusLabel(item: T): string {
    const statusField = this.getStatusField();
    if (!statusField) {
      return 'Inactivo';
    }

    const label = this.getFieldValue(item, statusField);
    return label === '--' ? 'Inactivo' : label;
  }

  public getCardBorderClass(item: T): string {
    const classes: Record<StatusTone, string> = {
      active: 'border-l-4 border-l-emerald-500 dark:border-l-emerald-800',
      warning: 'border-l-4 border-l-amber-500 dark:border-l-amber-800',
      danger: 'border-l-4 border-l-red-500 dark:border-l-red-800',
    };

    return classes[this.getStatusTone(item)];
  }

  public getStatusBadgeClass(item: T): string {
    const classes: Record<StatusTone, string> = {
      active:
        'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-800',
      warning:
        'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/30 dark:border-amber-800',
      danger:
        'text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-950/30 dark:border-red-800',
    };

    return classes[this.getStatusTone(item)];
  }

  public getStatusDotClass(item: T): string {
    const classes: Record<StatusTone, string> = {
      active: 'bg-emerald-500',
      warning: 'bg-amber-500',
      danger: 'bg-red-500',
    };

    return classes[this.getStatusTone(item)];
  }

  public search(value: string): void {
    // Emitir al Subject para aplicar debounce
    this.searchSubject.next(value);
  }

  public onStatusFilterSelect(value: string): void {
    if (value === 'all' || value === 'true' || value === 'false') {
      this.statusFilterChange.emit(value);
    }
  }

  public onClearFilters(): void {
    this.clearFilters.emit();
  }

  public activeFiltersCount(): number {
    let count = 0;

    if (this.searchValue().trim().length > 0) {
      count += 1;
    }

    if (this.statusFilterValue !== 'all') {
      count += 1;
    }

    return count;
  }

  public updateBreadcrumbs(breadcrumbs: MenuItem[]): void {
    this.breadcrumbs.set(breadcrumbs);
  }

  public setBreadcrumbRoute(route: string): void {
    this.breadcrumbRoute = route;
  }

  private getNestedValue(obj: unknown, path: string): unknown {
    return path.split('.').reduce<unknown>((current, key) => {
      if (
        current !== null &&
        typeof current === 'object' &&
        current !== undefined &&
        key in current
      ) {
        return (current as Record<string, unknown>)[key];
      }
      return null;
    }, obj);
  }

  private getFieldByKey(key: string): CardField | undefined {
    return this.cardConfig?.fields.find((field) => field.key === key);
  }

  private getStatusField(): CardField | undefined {
    return this.cardConfig?.fields.find((field) => field.type === 'status');
  }

  private getStatusTone(item: T): StatusTone {
    const statusField = this.getStatusField();
    if (!statusField) {
      return 'warning';
    }

    const rawValue = this.getNestedValue(item, statusField.nestedKey || statusField.key);
    const normalizedValue = String(rawValue ?? '')
      .trim()
      .toLowerCase();

    if (statusField.statusConfig && rawValue === statusField.statusConfig.activeValue) {
      return 'active';
    }

    if (['error', 'failed', 'failure', 'fail'].includes(normalizedValue)) {
      return 'danger';
    }

    if (
      ['inactive', 'disabled', 'false', 'warning', 'paused', 'stopped'].includes(normalizedValue)
    ) {
      return 'warning';
    }

    if (['active', 'enabled', 'true', 'success', 'running'].includes(normalizedValue)) {
      return 'active';
    }

    return statusField.statusConfig ? 'warning' : 'danger';
  }

  private onCardAction(action: CardActionType, item: T): void {
    this.cardAction.emit({ action, item });

    if (action === 'view-more') {
      this.onCardClick(item);
    }
  }

  private isItemEnabled(item: T): boolean {
    const statusField = this.getStatusField();
    if (!statusField) {
      return false;
    }

    const rawValue = this.getNestedValue(item, statusField.nestedKey || statusField.key);
    if (statusField.statusConfig) {
      return rawValue === statusField.statusConfig.activeValue;
    }

    return String(rawValue ?? '').toLowerCase() === 'true';
  }

  private setupSearch(): void {
    if (this.enableSearch) {
      console.log('Search setup - implement according to your needs');
    }
  }

  private loadData(): void {
    this.subscription?.unsubscribe();
    this.dataSource$.subscribe({
      next: (response) => {
        this.isLoading.set(false);
        const isSuccess = this.dataSourceConfig.successPath
          ? this.getNestedValue(response, this.dataSourceConfig.successPath)
          : true;

        if (isSuccess !== false) {
          const items = this.dataSourceConfig.dataPath
            ? (this.getNestedValue(response, this.dataSourceConfig.dataPath) as T[])
            : [];
          const totalRecords = this.dataSourceConfig.totalRecordsPath
            ? (this.getNestedValue(response, this.dataSourceConfig.totalRecordsPath) as number)
            : items.length;
          const totalPages = this.dataSourceConfig.totalPagesPath
            ? this.getNestedValue(response, this.dataSourceConfig.totalPagesPath)
            : Math.ceil(totalRecords / this.limit());

          // Sincronizar el limit con el backend si está disponible
          const backendLimit = this.getNestedValue(response, 'limit');
          if (typeof backendLimit === 'number') {
            this.limit.set(backendLimit);
          }

          this.items.set(items);
          // Ya no filtramos localmente, el backend lo hace
          this.filteredItems.set(items);
          this.totalRecords.set(totalRecords);
          if (typeof totalPages === 'number') {
            this.totalPages.set(totalPages);
          }

          // Restaurar el foco después de cargar los datos si es necesario
          if (this.shouldRestoreFocus && this.enableSearch) {
            this.restoreFocus();
          }
        }
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.items.set([]);
        this.filteredItems.set([]);
        this.shouldRestoreFocus = false;
      },
    });
  }

  private restoreFocus(): void {
    // Intentar restaurar el foco con múltiples intentos
    const tryRestoreFocus = (attempts = 0) => {
      if (this.searchInputRef?.nativeElement) {
        this.searchInputRef.nativeElement.focus();
        this.shouldRestoreFocus = false;
      } else if (attempts < 10) {
        // Intentar hasta 10 veces con un pequeño delay
        setTimeout(() => tryRestoreFocus(attempts + 1), 50);
      } else {
        // Si después de 10 intentos no está disponible, resetear el flag
        this.shouldRestoreFocus = false;
      }
    };
    setTimeout(() => tryRestoreFocus(), 100);
  }
}
